import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { featuredServices } from "../Services/servicesData";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";

function ServiceGrid() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Determine the number of cards to display based on screen size
  const displayedServices = isMobile
    ? featuredServices.slice(0, 4)
    : featuredServices.slice(0, 6);

  return (
    <Box
      sx={{
        position: "relative",
        py: { xs: 8, md: 10, lg: 12 },
        overflow: "hidden",
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: "absolute",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(251,241,239,0.8) 0%, rgba(255,255,255,0) 70%)",
          top: "-250px",
          right: "-100px",
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(251,241,239,0.6) 0%, rgba(255,255,255,0) 70%)",
          bottom: "-300px",
          left: "-200px",
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ mb: { xs: 6, md: 8 }, textAlign: "center" }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2.25rem", md: "3rem" },
              fontWeight: 700,
              position: "relative",
              display: "inline-block",
              mb: 3,
            }}
          >
            My Services
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 12, sm: 12, md: 12 }}
          sx={{ mb: { xs: 5, md: 6 } }}
        >
          {displayedServices.map((service, index) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              key={index}
              sx={{
                display: "flex",
              }}
            >
              <Card
                component={Link}
                to={`/services/${service.label
                  .toLowerCase()
                  .replace(/ /g, "-")}`}
                aria-label={`Learn more about ${service.label}`}
                variant="warm"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  overflow: "hidden",
                  position: "relative",
                  textDecoration: "none",
                  color: "inherit",
                  textAlign: "left",
                  "&:hover .service-arrow": {
                    color: "secondary.main",
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    p: { xs: 3, md: 4 },
                    flex: 1,
                  }}
                >
                  {/* Icon, title, and arrow row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      className="service-icon"
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: "10px",
                        backgroundColor: "secondary.light",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {typeof service.Icon === "string" ? (
                        <Box
                          component="img"
                          src={service.Icon}
                          alt=""
                          sx={{
                            width: 26,
                            height: "auto",
                            filter: "brightness(0.8)",
                          }}
                        />
                      ) : (
                        <service.Icon
                          sx={{
                            fontSize: 24,
                            color: "primary.main",
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      component="h3"
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1.05rem", md: "1.15rem" },
                        fontWeight: 700,
                        lineHeight: 1.25,
                        flex: 1,
                      }}
                    >
                      {service.label}
                    </Typography>

                    <ArrowRightAltIcon
                      className="service-arrow"
                      aria-hidden="true"
                      focusable="false"
                      sx={{
                        fontSize: 24,
                        color: "#999",
                        transition: "color 0.2s ease",
                      }}
                    />
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      color: "text.secondary", // Slightly darker for better contrast on pink
                      lineHeight: 1.6,
                    }}
                  >
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* View All Button */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            component={Link}
            to="/services"
            variant="peach"
            endIcon={<ArrowForwardIcon />}
            sx={{
              fontSize: { xs: "0.9rem", md: "1rem" },
              py: 1.5,
              px: 4,
            }}
          >
            View all Services
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default ServiceGrid;
